import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { fetchNationReportSubGroupsAsync } from "@features/report/subgroup/services/reportSubGroupSvc";
import {
    ReportSubGroup,
    ReportSubGroupListQuery,
    ReportSubGroupListResult,
} from "@features/report/subgroup/types/reportSubGroup";

export function useReportSubGroups(params: ReportSubGroupListQuery): ReportSubGroupListResult {
    const [current_page, setCurrentPage] = useState(params.page || 1);
    const [data, setData] = useState<ReportSubGroup[]>([]);
    const [from, setFrom] = useState<number | undefined>(undefined);
    const [last_page, setLastPage] = useState<number | undefined>(undefined);
    const [per_page, setPerPage] = useState<number | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const nationId = params.nationId;
    const queryParams = useMemo(
        () => ({
            page: params.page,
            perPage: params.perPage,
            all: params.all,
            freetext: params.freetext,
            subGroupName: params.subGroupName,
            subGroupId: params.subGroupId,
            description: params.description,
        }),
        [
            params.page,
            params.perPage,
            params.all,
            params.freetext,
            params.subGroupName,
            params.subGroupId,
            params.description,
        ]
    );

    const reload = useCallback(async () => {
        if (!nationId) {
            setData([]);
            setTotal(0);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            if (queryParams.all) {
                const allItems = await fetchAllPages<ReportSubGroup>((page: number) =>
                    fetchNationReportSubGroupsAsync(nationId, { ...queryParams, page })
                );

                setData(allItems);
                setTotal(allItems.length);
                setCurrentPage(1);
                setFrom(allItems.length > 0 ? 1 : undefined);
                setPerPage(allItems.length > 0 ? allItems.length : undefined);
                setLastPage(1);
                return;
            }

            const response = await fetchNationReportSubGroupsAsync(nationId, queryParams);
            if (response) {
                const result = response.getData();
                setCurrentPage(result?.current_page || 1);
                setData(result?.data || []);
                setFrom(result?.from);
                setLastPage(result?.last_page);
                setPerPage(result?.per_page);
                setTotal(result?.total || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [nationId, queryParams]);

    useEffect(() => {
        void reload();
    }, [reload]);

    return { data, total, current_page, from, per_page, last_page, loading, reload };
}
